import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import UserIcon from '../assets/icons/User.svg';
import SupportIcon from '../assets/icons/Support.svg';
import LogoutIcon from '../assets/icons/Logout.svg';

function Header({ onBurgerClick }) {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState(() => {
    if (typeof window === 'undefined') return '';
    return (
      localStorage.getItem('username') ||
      sessionStorage.getItem('username') ||
      ''
    );
  });
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleStorage = () => {
      setUsername(
        localStorage.getItem('username') ||
          sessionStorage.getItem('username') ||
          ''
      );
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (!username) {
      setProfile(null);
      setProfileLoading(false);
      setProfileError('');
      return undefined;
    }

    const controller = new AbortController();
    const loadProfile = async () => {
      try {
        setProfileLoading(true);
        setProfileError('');
        const response = await fetch(
          `/get_user_profile.php?username=${encodeURIComponent(username)}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        if (response.ok && data.success && data.user) {
          setProfile(data.user);
        } else {
          throw new Error(data.message || 'Unable to load profile');
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        setProfile(null);
        setProfileError('Unable to fetch account details');
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
    return () => controller.abort();
  }, [username]);

  const handleLogout = () => {
    setOpen(false);
    navigate('/login');
  };

  const displayName = profile?.name || username || 'Guest';
  const displayRole =
    profile?.admin_permission === '1' ? 'Administrator' : 'Standard User';

  return (
    <header className="relative z-30 w-full px-3 pt-3 md:px-6 md:pt-4">
      <div className="relative flex w-full items-center justify-between rounded-[2rem] border border-white/5 bg-[#10131a]/80 px-3 py-3 shadow-xl shadow-black/40 backdrop-blur-xl md:px-5">
        <div className="flex flex-1 items-center gap-3">
          <button
            onClick={onBurgerClick}
            className="group relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#bf1d2e] via-[#d63d4b] to-[#8c1120] text-white shadow-lg shadow-[#bf1d2e]/40 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#bf1d2e] focus-visible:ring-offset-2 focus-visible:ring-offset-[#10131a] md:hidden"
            aria-label="Open navigation"
          >
            <span className="absolute inset-0 bg-white/10 opacity-0 transition-opacity duration-300 group-hover:opacity-10" />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.8"
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex min-w-0 flex-1 items-center">
            <div className="flex items-center gap-2 rounded-full border border-white/5 bg-white/5 px-3 py-1.5 text-xs text-white/70 shadow-inner shadow-black/30 sm:px-3.5 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#bf1d2e] opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#bf1d2e]" />
              </span>
              <span className="whitespace-nowrap">Live Monitoring Active</span>
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <button
            ref={buttonRef}
            onClick={() => setOpen((prev) => !prev)}
            className="group relative flex items-center gap-3 rounded-2xl border border-white/5 bg-white/5 px-3 py-2 text-sm font-medium text-white shadow-lg shadow-black/30 transition-transform duration-300 hover:-translate-y-0.5 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#bf1d2e]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#10131a]"
            aria-expanded={open}
            aria-haspopup="menu"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-[#bf1d2e] to-[#f05263] p-1 text-white">
              <img src={UserIcon} alt="User" className="h-full w-full" />
            </span>
            <span className="hidden text-left leading-tight md:block">
              <span className="block text-xs text-white/60">
                {profileLoading ? 'Loading account…' : 'Welcome back'}
              </span>
              <span className="block text-sm font-semibold text-white">
                {profileLoading ? '—' : displayName}
              </span>
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-white/60 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <AnimatePresence>
            {open && (
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="absolute right-4 top-[calc(100%+0.75rem)] w-60 origin-top-right overflow-hidden rounded-2xl border border-white/10 bg-[#121620]/95 p-2 text-sm text-white shadow-2xl shadow-black/50 backdrop-blur-md md:right-6"
                role="menu"
              >
                <div className="rounded-xl bg-white/5 p-3">
                  <p className="text-xs uppercase tracking-[0.25em] text-white/40">Account</p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {profileLoading ? 'Loading…' : displayName}
                  </p>
                  <p className="text-xs text-white/40">
                    {profileLoading
                      ? 'Please wait'
                      : profile?.username || username || 'Unknown user'}
                  </p>
                  {!profileLoading && profile && (
                    <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-white/70">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#bf1d2e]" />
                      {displayRole}
                    </p>
                  )}
                  {profileError && !profileLoading && (
                    <p className="mt-2 text-xs text-rose-300">{profileError}</p>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  <Link
                    to="/profile"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between rounded-xl px-3 py-2 text-white/80 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                    role="menuitem"
                  >
                    <span>Profile</span>
                    <img src={UserIcon} alt="" className="h-4 w-4" />
                  </Link>
                  <a
                    href="mailto:service@digirockinnovations.com"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between rounded-xl px-3 py-2 text-white/80 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                    role="menuitem"
                  >
                    <span>Support</span>
                    <img src={SupportIcon} alt="" className="h-4 w-4" />
                  </a>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-white/80 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                    role="menuitem"
                  >
                    <span>Log Out</span>
                    <img src={LogoutIcon} alt="" className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-[#bf1d2e]/80 to-transparent md:inset-x-10" />
      </div>
    </header>
  );
}

export default Header;
